import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { formatDate as formatDatePreference } from "@/shared/datetime/formatters";

import { type Filter, isValueless } from "./FilterSchema";

dayjs.extend(utc);

const DEFAULT_VALUE_LIMIT = 2;

export interface FormatFilterValueOptions {
  /**
   * Max values rendered before collapsing into "+N more". Applies to every
   * multi-value type. Date BETWEEN (always two values by contract) is unaffected.
   */
  valueLimit?: number;
}

/**
 * The visible parts of a chip's value segment. The chip renders `text` and,
 * if present, a softer "+N more" tail. The fields are kept separate so the
 * tail can be styled distinctly without the helper returning React nodes.
 */
export interface FormattedFilterValue {
  /** Primary visible text — e.g. `"foo, bar"`, `"3/3/2025 → 3/5/2025"`. */
  text: string;
  /**
   * Count of remaining multi-values collapsed past `valueLimit`. Omitted
   * when nothing overflows or for non-multi-value types.
   */
  more?: number;
}

/**
 * Render the value-segment payload for a chip in `DataTable.ActiveFilters`.
 * Returns `null` when the comparator carries the full semantic on its own
 * (IS_NULL / IS_NOT_NULL / date presets) — callers should omit the value
 * segment entirely in that case.
 *
 * Inputs are assumed valid per `filterSchema`'s refine — empty arrays for
 * non-valueless comparators, malformed BETWEEN ranges, and NaN/Infinity
 * for numerics are rejected upstream by the per-type value inputs and the
 * schema refine. This helper does not re-validate.
 */
export function formatFilterValue(
  filter: Filter,
  opts: FormatFilterValueOptions = {},
): FormattedFilterValue | null {
  if (isValueless(filter.comparator)) {
    return null;
  }
  const limit = opts.valueLimit ?? DEFAULT_VALUE_LIMIT;

  switch (filter.type) {
    case "string":
      return joinWithLimit(filter.values, limit);
    // `metric` formats identically to `numeric` — stringify each number.
    case "numeric":
    case "metric":
      return joinWithLimit(filter.values.map(String), limit);
    case "set":
    case "enum":
      // Option-backed filters store labeled options, so the chip renders each
      // selection's own captured label — no option-list lookup needed.
      return joinWithLimit(
        filter.values.map((option) => option.label),
        limit,
      );
    case "boolean":
      // Schema's superRefine guarantees exactly one value for EQUALS
      // (IS_NULL/IS_NOT_NULL short-circuit above via `isValueless`).
      return { text: filter.values[0] ? "true" : "false" };
    case "date":
      if (filter.comparator === "BETWEEN") {
        return {
          text: `${formatDate(filter.values[0])} → ${formatDate(filter.values[1])}`,
        };
      }
      return { text: formatDate(filter.values[0]) };
  }
}

function joinWithLimit(parts: string[], limit: number): FormattedFilterValue {
  if (parts.length > limit) {
    return {
      text: parts.slice(0, limit).join(", "),
      more: parts.length - limit,
    };
  }
  return { text: parts.join(", ") };
}

// Dates are stored as ISO 8601 UTC-midnight strings (the date input's
// convention). We read the calendar day in UTC (so it stays stable
// regardless of the viewer's local timezone), then rebuild it as a local
// Date carrying that same Y/M/D so the user's active date-format preference
// (e.g. Swiss DD.MM.YYYY) applies without a timezone-induced day shift.
function formatDate(iso: string): string {
  const utcDay = dayjs.utc(iso);
  if (!utcDay.isValid()) {
    return iso;
  }
  return formatDatePreference(
    new Date(utcDay.year(), utcDay.month(), utcDay.date()),
    iso,
  );
}
